<script lang="ts" setup>

import { toTypedSchema } from '@vee-validate/zod';
import { useState } from 'nuxt/app';
import { useField, useForm } from 'vee-validate';
import { formatUnits, type Hash } from 'viem';
import { computed, onMounted, onUnmounted } from 'vue';
import * as zod from 'zod';

import { useAsyncData } from '#imports';
import { useMakeOffer } from '~/composables/web3/useMakeOffer';
import type { EvmOrderData } from '~/service/api/types';
import { isValidNumber } from '~/utils/helpers';

type ModalAcceptOfferProps = {
    order: EvmOrderData;
    price: Price;
    source: string;
    nftInfo: LiteNft;
    orderHash: Hash;
    tokensRemaining: number;
};

const props = defineProps<ModalAcceptOfferProps>();

const emit = defineEmits(['update:modelValue']);
const { user } = storeToRefs(useUserStore());

const {
    QueryWTFApiKey,
    nft
} = useWTFApi();

const {
    data: ownedCount,
    status: ownedStatusNftData,
    error: ownedErrorNFTData
} = useAsyncData(
    `${QueryWTFApiKey.NFT}-${props.nftInfo.collection.slug}-${props.nftInfo.identifier}-${user.value}`,
    async () => {
        if (user.value) {
            const response = await nft.getNft(
                props.nftInfo.collection.slug,
                props.nftInfo.identifier,
                user.value
            );
            return response.data.ownedCount;
        }
    },
    { watch: [user] }
);

watch(ownedCount, (newOwnedCount) => {
    if (newOwnedCount && newOwnedCount < props.tokensRemaining) {
        setFieldValue('quantity', newOwnedCount);
    }
});

const maxAmount = computed(() => ownedCount.value && ownedCount.value < props.tokensRemaining ? ownedCount.value : props.tokensRemaining);

const validationSchema = computed(() =>
    toTypedSchema(zod.object({
        quantity: zod
            .preprocess(
                val => (typeof val === 'number' ? val.toString() : val),
                zod
                    .string()
                    .optional()
                    .refine(val => val !== null && val !== undefined && isValidNumber(val) && Number(val) !== 0, { message: 'Invalid number format' })
                    .transform(val => Number(val))
                    .refine(val => val <= maxAmount.value, {
                        message: `You can accept the offer for ${maxAmount.value} tokens`
                    })
            )
    })));

const {
    meta,
    handleSubmit,
    setFieldValue
} = useForm({
    validationSchema,
    initialValues: {
        quantity: props.tokensRemaining
    },
    keepValuesOnUnmount: true
});

const { value: quantity, errorMessage: quantityError } = useField<string>('quantity');

const considerationReceipts = computed(() => props.order.parameters.consideration.filter(consideration => consideration.recipient !== props.order.parameters.offerer));

const source = computed(() => {
    const withCreators = considerationReceipts.value.length > 1 ? '/Creators' : '';
    const feeStr = considerationReceipts.value.length > 1 ? ' Fees' : ' Fee';
    return (props.source ? props.source.toUpperCase() : 'GMBZR') + withCreators + feeStr;
});

const symbolCurrency = props.price.symbol;
const allFeesValue = considerationReceipts.value.reduce((acc, item) => acc + BigInt(item.endAmount), BigInt('0'));
const sellerReceives = props.order.parameters.offer.reduce((acc, item) => acc + BigInt(item.endAmount), BigInt('0'));
const total = allFeesValue + sellerReceives;

const decimals = Math.log10(Number(total) / props.price.amount);

const amountPerOne = total / BigInt(props.tokensRemaining);

const commissionSize = computed(() => {
    const rawFeePercent = (allFeesValue * 10000n) / total;

    return Number(rawFeePercent) / 100;
});

const offerPrice = computed(() => {
    if (!quantity.value || !meta.value.valid) {
        return {
            afterFee: '',
            initialValue: ''
        };
    }

    const value = amountPerOne * BigInt(quantity.value);

    const feePercent = Math.round(commissionSize.value * 100);

    const fee = (value * BigInt(feePercent)) / 10000n;
    const afterFee = value - fee;

    return {
        afterFee: formatUnits(afterFee, decimals),
        initialValue: formatUnits(value, decimals)
    };
});

const { acceptOffer } = useMakeOffer(props.orderHash);

const onSubmit = handleSubmit(async ({ quantity }) => {
    await acceptOffer({
        order: props.order,
        orderHash: props.orderHash,
        network: props.nftInfo.network,
        amount: quantity,
        tokensRemaining: props.tokensRemaining,
        value: offerPrice.value.afterFee ? Number(offerPrice.value.afterFee) : 0,
        token: {
            name: props.nftInfo.name,
            amount: quantity,
            network: props.nftInfo.network,
            itemType: props.nftInfo.itemType,
            uuid: props.nftInfo.uuid,
            displayImageURL: props.nftInfo.displayImageURL,
            price: props.price,
            maxAmount: props.tokensRemaining
        }
    });
});

const initiatorKey = props.orderHash;

const { eventEmitter } = useEVMCore();

const modalEventsStore = useEventModalsStore();

const isLoading = useState(initiatorKey, () => false);

const getDescriptionForModal = (itemType: EvmItemType) => {
    let result: string[];
    result = [];
    if (isEvmErc721(itemType)) {
        result = [`${offerPrice.value.afterFee} ${symbolCurrency} Chain: ${props.nftInfo.network}`];
    }
    else if (isEvmErc1155(itemType)) {
        result = [`${quantity.value} ${Number(quantity.value) > 1 ? 'items' : 'item'} ${offerPrice.value.afterFee} ${symbolCurrency}`, `Chain: ${props.nftInfo.network}`];
    }

    return result;
};

onMounted(() => {
    eventEmitter.on('acceptOffer', {
        key: initiatorKey,
        onSuccess: (success) => {
            console.log(success);
            modalEventsStore.removeByType('approve');
            emit('update:modelValue', false);
            isLoading.value = false;
        },
        onError: () => {
            modalEventsStore.add(
                {
                    status: '',
                    type: 'error',
                    images: [props.nftInfo.displayImageURL ?? ''],
                    imageState: 'error',
                    title: props.nftInfo.name,
                    description: getDescriptionForModal(props.nftInfo.itemType),
                    heading: 'Error',
                    itemType: props.nftInfo.itemType,
                    content: 'Something went wrong, Try again later.'
                });
            isLoading.value = false;
        },
        onLoad: (loading) => {
            isLoading.value = loading;

            if (loading) {
                modalEventsStore.add(
                    {
                        status: '',
                        type: 'approve',
                        images: [props.nftInfo.displayImageURL ?? ''],
                        imageState: 'loading',
                        title: props.nftInfo.name,
                        description: getDescriptionForModal(props.nftInfo.itemType),
                        heading: 'Approve offer',
                        itemType: props.nftInfo.itemType,
                        content: 'You will be asked to review and confirm this offer from your wallet.'
                    });
            }
        }
    });
});

onUnmounted(() => {
    eventEmitter.off('buildOffer', initiatorKey);
});

</script>

<template>
  <form class="display-flex flex-column align-items-start position-relative width-full modal"
        @submit.prevent="onSubmit"
  >
    <h2 class="display-800-accent">
      Response to offer
    </h2>

    <div class="display-flex gap-16 align-items-center width-full modal-block">
      <ImgMain :alt="price?.token"
               :height="120"
               :src="nftInfo.displayImageURL"
               :width="120"
      />

      <div class="display-flex flex-column align-items-center width-full gap-32 ">
        <div class="display-flex align-items-center justify-between width-full">
          <p class="compact-400-accent color-text-grey">
            Floor price
          </p>
          <p class="compact-400-numeric color-text-grey">
            -
          </p>
        </div>

        <div class="display-flex align-items-center justify-between width-full">
          <p class="compact-400-accent color-text-grey">
            Best offer
          </p>
          <p class="compact-400-numeric color-text-grey">
            -
          </p>
        </div>
      </div>
    </div>
    <div class="modal-content">
      <div v-if="isEvmErc1155(props.nftInfo.itemType)"
           class="display-flex flex-column width-full gap-4 position-relative price"
      >
        <div class="display-flex flex-row justify-between">
          <p class="compact-500-accent mb-4">
            Quantity of items
          </p>
          <LoaderDots v-if="ownedStatusNftData === 'pending'" />
          <span v-else
                class="compact-500-accent text-color-secondary">{{ maxAmount }} available</span>
        </div>
        <input v-model="quantity"
               aria-label="Quantity of items"
               class="base-input compact-400-default ml-2"
               name="price"
               placeholder="Enter a quantity"
               type="number"
        >
        <Transition mode="out-in"
                    name="fade"
        >
          <span v-if="quantityError"
                class="position-absolute compact-300-accent modal-price__error"
          >{{ quantityError }}</span>
        </Transition>
      </div>
      <div class="display-flex flex-row justify-between modal-block modal-block--border">
        <span>Offered Price</span>
        <span>{{ offerPrice.initialValue || '-' }} {{ symbolCurrency }}</span>
      </div>
      <div class="modal-block display-flex gap-16 flex-column modal-block--border">
        <div class="display-flex align-items-center justify-between gap-16">
          <span class="compact-400-numeric text-color-secondary">{{ source }}</span>
          <span v-if="commissionSize"
                class="compact-400-numeric text-color-secondary"
          >{{ commissionSize }} %</span>
          <span v-else
                class="compact-400-numeric text-color-secondary"
          >-</span>
        </div>
        <div v-if="offerPrice"
             class="display-flex align-items-center justify-between gap-16"
        >
          <span class="compact-500-accent">Total Earnings</span>
          <span class="compact-500-accent"
          >{{ offerPrice.afterFee || '-' }} {{ symbolCurrency }}</span>
        </div>
      </div>
    </div>
    <Button :is-disabled="!meta.valid"
            :is-loading="isLoading"
            class="width-full"
            color="bg.mojito"
            size="size.medium"
    >
      <button class="compact-500-accent display-flex justify-center gap-8 align-items-center"
              type="submit"
      >
        <span class="compact-500-accent">Accept</span>
        <IconMain :size="24"
                  icon="arrow-r-l"
        />
      </button>
    </Button>
  </form>
</template>

<style lang="scss" scoped>

.modal {
  max-height: 100%;

  &__img {
    width: 120px;
    height: 120px;
  }

  &-block {
    padding: 34px 0;

    &--border {
      border-top: 1px solid rgb(233 234 236 / 20%);
    }
  }

  &-price {
    &__error {
      bottom: -18px;
      color: var(--content-negative);
    }
  }

  &-content {
    width: calc(100% + #{$spacing-400});
    margin-right: -$spacing-400;
    padding-right: $spacing-400;
    overflow: auto;
  }
}

</style>
